import React, { useEffect, useState } from 'react';
import { assignPlanToArtist } from '../services/artistSubscriptionService';
import { getPlans, Plan } from '../services/planService';
import api from '../api/axiosClient';
import { toast } from 'react-toastify';

interface AssignPlanModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface Artist { artistId: number; name: string; isActive?: boolean; active?: boolean; status?: string; }

const AssignPlanModal: React.FC<AssignPlanModalProps> = ({ open, onClose, onSuccess }) => {
  const [artists, setArtists] = useState<Artist[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [artistId, setArtistId] = useState<number | ''>('');
  const [planId, setPlanId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Fetch makeup artists from admin page; filter to active ones
    api.get('/MakeupArtist')
      .then(res => {
        const rows = Array.isArray(res.data) ? res.data : [];
        // Map to Artist interface and filter active
        const mapped: Artist[] = rows.map((ma: any) => ({
          artistId: ma.id,
          name: ma.displayName || ma.name || 'Unknown',
          active: ma.active
        }));
        const active = mapped.filter(a => a.active === true);
        setArtists(active);
      })
      .catch(() => setArtists([]));
    getPlans().then(setPlans).catch(() => setPlans([]));
  }, [open]);

  useEffect(() => {
    if (!planId) return;
    const plan = plans.find(p => p.planId === planId);
    const today = new Date();
    setStartDate(today.toISOString().slice(0,10));
    if (plan) {
      const end = new Date(today);
      end.setDate(end.getDate() + plan.durationInDays);
      setEndDate(end.toISOString().slice(0,10));
    } else { setEndDate(''); }
  }, [planId, plans]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!artistId || !planId) { toast.error('Select artist and plan'); return; }
    setLoading(true);
    try {
      await assignPlanToArtist({ artistId, planId, transactionId });
      toast.success('Plan assigned');
      onSuccess();
      onClose();
    } catch { toast.error('Failed to assign plan'); }
    finally { setLoading(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button className="absolute top-2 right-2 text-gray-500" onClick={onClose}>×</button>
        <h2 className="text-xl font-bold mb-4">Assign Plan to Artist</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-1 font-medium">Artist *</label>
            <select className="w-full border rounded px-3 py-2" value={artistId} onChange={e => setArtistId(Number(e.target.value))} required>
              <option value="">Select artist</option>
              {artists.map(a => (<option key={a.artistId} value={a.artistId}>{a.name}</option>))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block mb-1 font-medium">Plan *</label>
            <select className="w-full border rounded px-3 py-2" value={planId} onChange={e => setPlanId(Number(e.target.value))} required>
              <option value="">Select plan</option>
              {plans.map(p => (<option key={p.planId} value={p.planId}>{p.planName}</option>))}
            </select>
          </div>
          <div className="mb-4 flex gap-4">
            <div>
              <label className="block mb-1 font-medium">Start Date</label>
              <input type="date" className="border rounded px-3 py-2" value={startDate} readOnly />
            </div>
            <div>
              <label className="block mb-1 font-medium">End Date</label>
              <input type="date" className="border rounded px-3 py-2" value={endDate} readOnly />
            </div>
          </div>
          <div className="mb-4">
            <label className="block mb-1 font-medium">Transaction ID</label>
            <input type="text" className="w-full border rounded px-3 py-2" value={transactionId} onChange={e => setTransactionId(e.target.value)} placeholder="Optional" />
          </div>
          <div className="flex gap-4 mt-6">
            <button type="submit" className="bg-primary text-white px-4 py-2 rounded" disabled={loading}>{loading ? 'Assigning...' : 'Assign Plan'}</button>
            <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded" onClick={onClose} disabled={loading}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignPlanModal;
